import {
  useMutation,
  useSuspenseQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { trpc } from "app/trpc"
import { useCurrentOrganization } from "app/core/hooks/useCurrentOrganization"
import { useCurrentUser } from "app/core/hooks/useCurrentUser"
import { usePosthogWithCurrentUser } from "app/core/hooks/usePosthogWithCurrentUser"
import usePrevious from "app/core/hooks/usePrevious"
import {
  OrganizationUserRole,
  DiscoveryTourStatusType,
  SubscriptionType,
} from "db/enums"
import { parseAsStringEnum, useQueryState } from "nuqs"
import { useCallback, useEffect, useMemo, useState } from "react"
import type { GetAllDiscoveriesResult } from "../queries/getAllDiscoveries"

export default function useDiscoveryTours({
  openWelcomeTour,
  openSchoolManagementTour,
  setOpenWelcomeTour,
  setOpenSchoolManagementTour,
}: {
  openWelcomeTour?: boolean
  openSchoolManagementTour?: boolean
  setOpenWelcomeTour?: (show: boolean) => void
  setOpenSchoolManagementTour?: (show: boolean) => void
}) {
  const currentUser = useCurrentUser()
  const currentOrganization = useCurrentOrganization()
  const analytics = usePosthogWithCurrentUser(currentUser)

  const [activeTour, setActiveTour] = useState<
    GetAllDiscoveriesResult[0] | null
  >(null)
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0)

  const goToNextStep = useCallback(() => {
    if (activeTour && activeStepIndex < activeTour.steps.length - 1) {
      setActiveStepIndex(activeStepIndex + 1)
    }
  }, [activeTour, activeStepIndex])

  const goToPreviousStep = useCallback(() => {
    if (activeTour && activeStepIndex > 0) {
      setActiveStepIndex(activeStepIndex - 1)
    }
  }, [activeTour, activeStepIndex])

  const hasNextStep = useMemo(() => {
    if (!activeTour) return false
    return activeStepIndex < activeTour.steps.length - 1
  }, [activeTour, activeStepIndex])

  const hasPreviousStep = useMemo(() => {
    if (!activeTour) return false
    return activeStepIndex > 0
  }, [activeTour, activeStepIndex])

  const selectTour = useCallback(
    (tour: GetAllDiscoveriesResult[0], tourAutoTriggered?: boolean) => {
      analytics.capture({
        name: "discovery_tour_selected",
        properties: {
          tourId: tour.id,
          tourName: tour.displayTitle,
          tourAutoTriggered: !!tourAutoTriggered,
        },
      })
      setActiveTour(tour)
      setActiveStepIndex(0)
    },
    [analytics]
  )

  const queryClient = useQueryClient()
  const markDiscoveryCompletedMutation = useMutation(
    trpc.support.markDiscoveryCompleted.mutationOptions()
  )

  const returnToOverview = useCallback(() => {
    setActiveTour(null)
    setActiveStepIndex(0)
  }, [])

  // Query parameters
  const [subscriptionType] = useQueryState(
    "subscriptionType",
    parseAsStringEnum<SubscriptionType>(
      Object.values(SubscriptionType) as SubscriptionType[]
    )
  )
  const [organizationUserRole] = useQueryState(
    "organizationUserRole",
    parseAsStringEnum<OrganizationUserRole>(
      Object.values(OrganizationUserRole) as OrganizationUserRole[]
    )
  )

  const finishTour = useCallback(
    async (tour: GetAllDiscoveriesResult[0]) => {
      await markDiscoveryCompletedMutation.mutateAsync({
        discoveryId: tour.id,
      })
      await queryClient.invalidateQueries({
        queryKey: trpc.support.getAllDiscoveries.queryKey({
          subscriptionType:
            currentUser?.role === "ADMIN"
              ? subscriptionType || "SCHOOL_SUBSCRIPTION"
              : (currentOrganization?.subscription
                  .subscriptionType as SubscriptionType | null),
          organizationUserRole:
            currentUser?.role === "ADMIN"
              ? organizationUserRole || OrganizationUserRole.MANAGER
              : currentUser?.organizationUsers?.[0]?.role || null,
        }),
      })
      await returnToOverview()
      setOpenWelcomeTour?.(false)
      setOpenSchoolManagementTour?.(false)
    },
    [
      markDiscoveryCompletedMutation,
      returnToOverview,
      setOpenWelcomeTour,
      setOpenSchoolManagementTour,
      queryClient,
      currentUser,
      currentOrganization,
      subscriptionType,
      organizationUserRole,
    ]
  )

  const completeTour = useCallback(
    async (tour: GetAllDiscoveriesResult[0]) => {
      await markDiscoveryCompletedMutation.mutateAsync({
        discoveryId: tour.id,
      })
      await queryClient.invalidateQueries({
        queryKey: trpc.support.getAllDiscoveries.queryKey({
          subscriptionType:
            currentUser?.role === "ADMIN"
              ? subscriptionType || "SCHOOL_SUBSCRIPTION"
              : (currentOrganization?.subscription
                  .subscriptionType as SubscriptionType | null),
          organizationUserRole:
            currentUser?.role === "ADMIN"
              ? organizationUserRole || OrganizationUserRole.MANAGER
              : currentUser?.organizationUsers?.[0]?.role || null,
        }),
      })
    },
    [
      markDiscoveryCompletedMutation,
      queryClient,
      currentUser,
      currentOrganization,
      subscriptionType,
      organizationUserRole,
    ]
  )

  const { data: discoveryTours, isLoading: isDiscoveryLoading } =
    useSuspenseQuery({
      ...trpc.support.getAllDiscoveries.queryOptions({
        subscriptionType:
          currentUser?.role === "ADMIN"
            ? subscriptionType || "SCHOOL_SUBSCRIPTION"
            : (currentOrganization?.subscription
                .subscriptionType as SubscriptionType | null),
        organizationUserRole:
          currentUser?.role === "ADMIN"
            ? organizationUserRole || OrganizationUserRole.MANAGER
            : currentUser?.organizationUsers?.[0]?.role || null,
      }),
      staleTime: currentUser?.role === "ADMIN" ? 0 : 1000 * 60 * 60, // 1 hour
    })

  const [hasLoadedSpecificTour, setHasLoadedSpecificTour] = useState(false)
  // Check if we have missing prerequisites and should show loading
  const isLoading = useMemo(() => {
    if (!currentUser) return true

    if (currentUser.role === "ADMIN") {
      if (isDiscoveryLoading || !discoveryTours || !currentUser) {
        return true
      }
      return false
    }
    if (
      (openWelcomeTour || openSchoolManagementTour) &&
      (!hasLoadedSpecificTour || !activeTour) &&
      isDiscoveryLoading
    ) {
      return true
    }

    // Fallback, if we're no longer loading, but couldn't load a specific tour, still open modal on
    // the main screen
    if (
      (openWelcomeTour || openSchoolManagementTour) &&
      hasLoadedSpecificTour &&
      !activeTour &&
      !isDiscoveryLoading
    ) {
      return false
    }

    if (
      isDiscoveryLoading ||
      !discoveryTours ||
      !currentUser ||
      !currentOrganization
    ) {
      return true
    }

    return false
  }, [
    isDiscoveryLoading,
    discoveryTours,
    currentUser,
    currentOrganization,
    openWelcomeTour,
    openSchoolManagementTour,
    hasLoadedSpecificTour,
    activeTour,
  ])

  const completedToursCount = useMemo(() => {
    const completedTours = discoveryTours.filter((tour) =>
      tour.completionStatuses.some(
        (status) => status.status === DiscoveryTourStatusType.COMPLETED
      )
    )
    return completedTours.length
  }, [discoveryTours])

  useEffect(() => {
    if (discoveryTours.length > 0 && !hasLoadedSpecificTour) {
      if (openWelcomeTour) {
        const welcomeTour = discoveryTours.find((tour) => tour.isWelcomeTour)
        if (welcomeTour) {
          selectTour(welcomeTour, true)
          setOpenWelcomeTour?.(false)
        }
      } else if (openSchoolManagementTour) {
        const schoolManagementTour = discoveryTours.find(
          (tour) => tour.isSchoolManagementTour
        )
        if (schoolManagementTour) {
          selectTour(schoolManagementTour, true)
          setOpenSchoolManagementTour?.(false)
        }
      }
      // NOTE: This has to be wrapped in an if statement, so that if openWelcomeTour or openSchoolManagementTour
      // Is set after the render, the rerender will force the tour to be loaded
      // However it should not be set if no tour is being loaded
      if (openWelcomeTour || openSchoolManagementTour) {
        setHasLoadedSpecificTour(true)
      }
    }
  }, [
    discoveryTours,
    openWelcomeTour,
    openSchoolManagementTour,
    hasLoadedSpecificTour,
    setOpenWelcomeTour,
    setOpenSchoolManagementTour,
    selectTour,
  ])

  return {
    discoveryTours,
    isLoading,
    currentUser,
    activeTour,
    activeStepIndex,
    goToNextStep,
    goToPreviousStep,
    hasNextStep,
    hasPreviousStep,
    returnToOverview,
    completedToursCount,
    selectTour,
    finishTour,
    completeTour,
  } as const
}
